/* global TYPESENSE_CONFIG */
import algoliasearch from 'algoliasearch/lite'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'

// The engine seam: everything above this file is engine-agnostic, everything
// engine-specific lives here. Falls back to Algolia when TYPESENSE_CONFIG is
// missing or has no host. See docs/search/typesense-migration.md.

// Reads TYPESENSE_CONFIG, not a DOM element — called off the PLP too.
export function typesenseEnabled () {
  if (typeof TYPESENSE_CONFIG === 'undefined') return false

  return TYPESENSE_CONFIG.enabled === true && !!TYPESENSE_CONFIG.host
}

// Stopword guard: an all-stopword query returns zero results on Typesense.
// Mirrors Search::Stopwords#safe_for? — keep the two in step, diacritic
// folding included (an accented stopword would otherwise slip the guard).
function foldForTokenizing (text) {
  return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function stopwordsSafeFor (query, stopwords) {
  if (!stopwords || stopwords.length === 0) return true

  const tokens = foldForTokenizing(query).split(/[^a-z0-9]+/).filter(Boolean)
  if (tokens.length === 0) return true

  const folded = stopwords.map(foldForTokenizing)

  return tokens.some((token) => !folded.includes(token))
}

// HA client shape: the load-balanced endpoint as nearestNode plus the
// individual nodes as fallbacks for the node-rotation draining window.
// Single-node clusters set no TYPESENSE_NODES and keep one plain node.
export function typesenseServerConfig () {
  const { host, port, protocol, nodes } = TYPESENSE_CONFIG
  const fallbackHosts = nodes || []

  if (fallbackHosts.length === 0) return { nodes: [{ host, port, protocol }] }

  return {
    nearestNode: { host, port, protocol },
    nodes: fallbackHosts.map((fallbackHost) => ({ host: fallbackHost, port, protocol }))
  }
}

// Typesense's cross-user result cache (distinct from the per-browser
// cacheSearchResultsForSeconds). Mirrors Search::QueryBuilder#cache_params:
// per-user sort_by and the sponsored client must not share it.
function serverCacheEnabled (overrides) {
  if (TYPESENSE_CONFIG.searchParameters.use_cache !== true) return false
  if (overrides.sort_by) return false
  if (overrides.drop_tokens_threshold === 0) return false

  return true
}

// typesense-js defaults to 5s per node; a personalised PLP batch has been
// measured past that on a busy cluster. Margin, not the fix.
const CONNECTION_TIMEOUT_SECONDS = 10

function buildTypesenseAdapter (overrides = {}) {
  const { searchApiKey, searchParameters } = TYPESENSE_CONFIG
  const stopwords = searchParameters.stopwords || []

  const adapter = new TypesenseInstantSearchAdapter({
    server: {
      apiKey: searchApiKey,
      ...typesenseServerConfig(),
      cacheSearchResultsForSeconds: 120,
      useServerSideSearchCache: serverCacheEnabled(overrides),
      connectionTimeoutSeconds: CONNECTION_TIMEOUT_SECONDS
    },

    // Canonical relevance config, rendered from Search::QueryBuilder.
    additionalSearchParameters: {
      query_by: searchParameters.query_by,
      query_by_weights: searchParameters.query_by_weights,
      num_typos: searchParameters.num_typos,
      min_len_1typo: searchParameters.min_len_1typo,
      min_len_2typo: searchParameters.min_len_2typo,
      drop_tokens_threshold: searchParameters.drop_tokens_threshold,
      exclude_fields: searchParameters.exclude_fields,
      per_page: searchParameters.per_page,
      sort_by: searchParameters.sort_by,
      // Overridden per query below when a query is nothing but stopwords.
      stopwords: searchParameters.stopwords_set || undefined,
      // Approximate faceting (Search::Facets). Must stay exactly what the
      // server sent, or the hydrated first paint and this query count the
      // same page two different ways.
      ...(searchParameters.facet_sampling || {}),
      ...overrides
    },

    // Must stay false on v30: true makes the adapter send `override_tags`,
    // which v30 accepts and silently ignores — every ruleContext would stop
    // working with no error.
    useOverrideTags: false
  })

  return withQueryEcho(withCollectionGuard(withStopwordGuard(withFacetNarrowing(withoutRedundantFacetRequests(withCheapSortOnFacetRequests(adapter))), stopwords)))
}

// The adapter sends `*` (Typesense's match-all) for a query-less search and
// echoes it back. Algolia echoed the empty query, and consumers read the echo
// as the shopper's words: on-page copy, the impressions' search_list. Server
// twin: Search::AlgoliaCompat#echoed_query.
const MATCH_ALL_QUERY = '*'

function withQueryEcho (adapter) {
  const client = adapter.searchClient
  const originalSearch = client.search.bind(client)

  client.search = (requests) => originalSearch(requests).then((response) => ({
    ...response,
    results: (response.results || []).map((result) =>
      (result && result.query === MATCH_ALL_QUERY) ? { ...result, query: '' } : result)
  }))

  return adapter
}

export const DEFAULT_RANKING_FIELD = 'compacted_boost_score'

// The merchandising float in the last sort slot: compacted_boost_score, or a
// ranking variant's float when ?force_ranking picked one server-side
// (Search::RankingVariant). Read at call time, not import time, so it always
// matches the rendered TYPESENSE_CONFIG.
export function rankingField () {
  const params = (typeof TYPESENSE_CONFIG !== 'undefined' && TYPESENSE_CONFIG.searchParameters) || {}

  return params.ranking_field || DEFAULT_RANKING_FIELD
}

// The adapter stamps the personalised `_eval` sort_by on every request in the
// batch; facet-count requests (hitsPerPage 0) get a cheap sort via `/sort/` instead.
export function facetRequestSort () {
  return `${rankingField()}:desc`
}

export function facetRequestIndexName (indexName, collection) {
  if (!indexName || String(indexName).includes('/sort/')) return indexName

  return `${collection}/sort/${facetRequestSort()}`
}

function isFacetOnlyRequest (request) {
  return request.params && request.params.hitsPerPage === 0 && !request.params.facetQuery
}

function withCheapSortOnFacetRequests (adapter) {
  const collection = TYPESENSE_CONFIG.collection
  const client = adapter.searchClient
  const originalSearch = client.search.bind(client)

  client.search = (requests) => originalSearch(requests.map((request) => {
    if (!isFacetOnlyRequest(request)) return request

    return { ...request, indexName: facetRequestIndexName(request.indexName, collection) }
  }))

  return adapter
}

// Facet-only requests narrowed to no facets get an empty result; ones filtered
// like the hits request get a copy of its counts. Each keeps its slot. See
// docs/search/typesense-relevance.md (Redundant facet-count requests).
const facetsOf = (request) => [(request.params && request.params.facets) || []].flat()
const listOf = (value) => (value === undefined || value === null || value === '') ? [] : [value].flat()

function sameFilters (request, main) {
  return ['query', 'filters', 'facetFilters', 'numericFilters', 'tagFilters'].every((key) =>
    JSON.stringify(listOf(request.params[key])) === JSON.stringify(listOf(main.params[key])))
}

function pick (object, keys) {
  if (!object) return undefined
  return keys.reduce((picked, key) => (key in object ? { ...picked, [key]: object[key] } : picked), {})
}

export function planFacetRequests (requests) {
  let main
  return requests.map((request) => {
    if (!isFacetOnlyRequest(request)) {
      main = request
      return { send: true }
    }
    if (facetsOf(request).length === 0) return { empty: true }
    if (main && main.indexName === request.indexName && sameFilters(request, main)) return { copyFrom: requests.indexOf(main) }
    return { send: true }
  })
}

function emptyFacetResult (request) {
  return { hits: [], nbHits: 0, page: 0, nbPages: 0, hitsPerPage: 0, processingTimeMS: 0, query: request.params.query || '', params: '', facets: {} }
}

function withoutRedundantFacetRequests (adapter) {
  const client = adapter.searchClient
  const originalSearch = client.search.bind(client)

  client.search = (requests) => {
    const plan = planFacetRequests(requests)
    if (plan.every((step) => step.send)) return originalSearch(requests)

    const sentIndices = plan.flatMap((step, index) => step.send ? [index] : [])
    const searched = sentIndices.length > 0
      ? originalSearch(sentIndices.map((index) => requests[index]))
      : Promise.resolve({ results: [] })

    return searched.then((response) => {
      const byIndex = {}
      sentIndices.forEach((requestIndex, position) => { byIndex[requestIndex] = response.results[position] })

      return {
        ...response,
        results: requests.map((request, index) => {
          const step = plan[index]
          if (step.send) return byIndex[index]
          if (step.empty) return emptyFacetResult(request)

          const main = byIndex[step.copyFrom]
          return {
            ...emptyFacetResult(request),
            facets: pick(main.facets, facetsOf(request)) || {},
            facets_stats: pick(main.facets_stats, facetsOf(request)),
            exhaustiveFacetsCount: main.exhaustiveFacetsCount
          }
        })
      }
    })
  }

  return adapter
}

// Facet narrowing — the client half of Search::Facets. InstantSearch asks
// for counts on every facet the helper declares (~50), but only the
// WIDGET_COUNTS list renders any, so the request is narrowed here while the
// declarations (which make URL refinements work) stay. Wraps `search` only —
// searchForFacetValues is already minimal and must keep its field.
function withFacetNarrowing (adapter) {
  const wanted = (TYPESENSE_CONFIG.searchParameters || {}).facet_counts

  // No list rendered (older cached layout): send what InstantSearch asked
  // for. Slower, never wrong.
  if (!Array.isArray(wanted) || wanted.length === 0) return adapter

  const client = adapter.searchClient
  const originalSearch = client.search.bind(client)

  client.search = (requests) => originalSearch(requests.map((request) => {
    const facets = request.params && request.params.facets
    if (!facets) return request

    return { ...request, params: { ...request.params, facets: [facets].flat().filter((facet) => wanted.includes(facet)) } }
  }))

  return adapter
}

// Collection guard — last line of defence against a stale index name.
// Algolia-era URLs carry replica index names; an unknown collection 404s and
// the adapter THROWS, rendering an empty grid. Pinning the collection (while
// preserving any `/sort/` suffix) closes every door at once. Safe: every
// caller of this adapter queries the listings collection; autocomplete never
// comes through here.
export function knownCollectionIndexName (indexName, collection) {
  if (!indexName) return collection

  const [named, sort] = String(indexName).split('/sort/')
  if (named === collection) return indexName

  return sort ? `${collection}/sort/${sort}` : collection
}

// Reported once per page: we want the signal, not the volume.
let reportedRewrite = false

function reportRewrittenIndexName (from, to) {
  if (reportedRewrite) return
  reportedRewrite = true

  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.captureMessage(`[typesense] rewrote unknown index name ${from} -> ${to}`, 'info')
  }
}

function withCollectionGuard (adapter) {
  const collection = TYPESENSE_CONFIG.collection
  const client = adapter.searchClient
  const originalSearch = client.search.bind(client)

  client.search = (requests) => originalSearch(requests.map((request) => {
    const indexName = knownCollectionIndexName(request.indexName, collection)
    if (indexName === request.indexName) return request

    reportRewrittenIndexName(request.indexName, indexName)
    return { ...request, indexName }
  }))

  return adapter
}

function withStopwordGuard (adapter, stopwords) {
  if (stopwords.length === 0) return adapter

  const params = adapter.configuration.additionalSearchParameters
  const configuredSet = params.stopwords
  if (!configuredSet) return adapter

  const client = adapter.searchClient
  const originalSearch = client.search.bind(client)

  client.search = (requests) => {
    // Batched requests share one parameter object, so the set stays on only
    // if every query in the batch survives the stripping.
    const allSafe = requests.every((request) =>
      stopwordsSafeFor(request.params && request.params.query, stopwords)
    )

    if (allSafe) {
      params.stopwords = configuredSet
    } else {
      delete params.stopwords
    }

    return originalSearch(requests)
  }

  return adapter
}

// InstantSearch's searchClient. `overrides` are additionalSearchParameters
// for the Typesense adapter, ignored on Algolia.
export function buildSearchClient (overrides = {}) {
  if (typesenseEnabled()) {
    return { client: memoisedAdapter(overrides), engine: 'typesense' }
  }

  return {
    client: algoliasearch(
      process.env.ALGOLIASEARCH_APPLICATION_ID,
      process.env.ALGOLIASEARCH_API_KEY_SEARCH
    ),
    engine: 'algolia'
  }
}

export function similarQueryParameters () {
  if (typeof TYPESENSE_CONFIG === 'undefined') return {}

  return (TYPESENSE_CONFIG.searchParameters && TYPESENSE_CONFIG.searchParameters.similar_query) || {}
}

// Algolia's restrictSearchableAttributes has no per-request equivalent here
// (the adapter drops it), so narrowing is a query_by override — sliced
// together with its two positional companions, or Typesense rejects the
// mismatched list lengths.
export function restrictQueryBy (fields) {
  if (typeof TYPESENSE_CONFIG === 'undefined') return {}

  const params = TYPESENSE_CONFIG.searchParameters || {}
  const all = String(params.query_by || '').split(',')
  const weights = String(params.query_by_weights || '').split(',')
  const typos = String(params.num_typos || '').split(',')

  const positions = fields.map((field) => all.indexOf(field)).filter((index) => index >= 0)

  // An unknown field would narrow the search to nothing; fall back to the
  // full query_by instead.
  if (positions.length !== fields.length) return {}

  const slice = (list) => {
    const picked = positions.map((index) => list[index])

    return picked.every((value) => value !== undefined && value !== '') ? picked.join(',') : null
  }

  const overrides = { query_by: positions.map((index) => all[index]).join(',') }
  const slicedWeights = slice(weights)
  const slicedTypos = slice(typos)
  if (slicedWeights) overrides.query_by_weights = slicedWeights
  if (slicedTypos) overrides.num_typos = slicedTypos

  return overrides
}

// The adapter drops anything that is not additionalSearchParameters or a
// known InstantSearch key, so parameters that cannot travel on a request —
// the similar-query profile, the per-user affinity sort_by, never-broaden,
// a narrowed query_by — are baked into the adapter here. Always build through
// this one function: memoisedAdapter keys its cache on JSON.stringify, so key
// order matters.
export function adapterOverrides ({ similarQuery = false, restrictToTitle = false, seekrSortBy = null, neverBroaden = false } = {}) {
  const overrides = {}

  // Order fixed for cache-key stability; restrictToTitle overwrites the
  // similar-query profile's query_by, neverBroaden overwrites its threshold,
  // and a hard sort (in the index name) still wins over sort_by.
  if (similarQuery) Object.assign(overrides, similarQueryParameters())
  if (restrictToTitle) Object.assign(overrides, restrictQueryBy(['title']))
  if (neverBroaden) overrides.drop_tokens_threshold = 0
  if (seekrSortBy) overrides.sort_by = seekrSortBy

  return overrides
}

// Puts the similar-query title into `q` on the way out. Must happen on the
// built request, not configure({query}) — the hidden searchBox widget resets
// `query` on every render and wins. Also keeps the URL on ?similar_query=.
export function withSimilarQuery (queries, similarquery, onTypesense) {
  if (!onTypesense || !similarquery) return queries

  return queries.map((request) => ({
    ...request,
    params: {
      ...request.params,
      query: (request.params && request.params.query) || similarquery
    }
  }))
}

const adapterCache = new Map()

// One adapter per distinct override set, for the life of the page — building
// per call would throw away the response cache.
function memoisedAdapter (overrides = {}) {
  const key = JSON.stringify(overrides)
  if (!adapterCache.has(key)) {
    adapterCache.set(key, buildTypesenseAdapter(overrides).searchClient)
  }

  return adapterCache.get(key)
}

// Algolia-only parameters that must not reach the adapter.
const ALGOLIA_ONLY_PARAMS = [
  'attributesToRetrieve',
  'clickAnalytics',
  'analytics',
  'userToken',
  'getRankingInfo',
  'similarQuery',
  'optionalFilters',
  'removeWordsIfNoResults'
]

export function toTypesenseParams (params = {}) {
  const cleaned = { ...params }
  ALGOLIA_ONLY_PARAMS.forEach((key) => delete cleaned[key])

  return cleaned
}

// Algolia's `Index#search(query, params)` over Typesense.
export function typesenseSearchIndex (collection, overrides = {}) {
  const client = memoisedAdapter(overrides)

  return {
    search (query, params = {}) {
      return client
        .search([{
          indexName: collection,
          params: { ...toTypesenseParams(params), query: query || '' }
        }])
        .then((response) => response.results[0])
    }
  }
}

// The index/collection the PLP should query. On Typesense this is an alias,
// so a zero-downtime rebuild swaps underneath with no client change.
export function primaryIndexName (algoliaIndexName) {
  return typesenseEnabled() ? TYPESENSE_CONFIG.collection : algoliaIndexName
}

// Sort selector value: Algolia's per-sort replica indexes collapse into
// `<collection>/sort/<field>:<direction>` strings against one collection.
export function typesenseSortIndex (collection, field, direction) {
  return `${collection}/sort/${field}:${direction}`
}

// Sponsored: a filter plus a sort on the main collection. The sort travels
// in the index name because the adapter derives sort_by from it.
export const SPONSORED_FILTER = 'vinterior_sponsored_listing:true'

export function typesenseSponsoredIndex (collection) {
  return `${collection}/sort/${TYPESENSE_CONFIG.searchParameters.sponsored_sort_by}`
}

// A second adapter instance, identical but for drop_tokens_threshold: 0 —
// never broaden. It cannot be a per-request parameter (the adapter drops it)
// and the shared config cannot be mutated (the sponsored search races the
// main one). An empty sponsored slot beats loosely-related paid placements.
export function buildSponsoredSearchClient () {
  return memoisedAdapter(adapterOverrides({ neverBroaden: true }))
}

// ANDs the sponsored filter onto whatever the page already filters by, so a
// sponsored placement still respects the current category and refinements.
export function withSponsoredFilter (existingFilters) {
  const current = (existingFilters || '').trim()
  if (current === '') return SPONSORED_FILTER

  return `(${current}) && ${SPONSORED_FILTER}`
}
